import { Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { LaskunLahetystyyppi } from '@lemon/core-firebase/model/lasku'
import { TilastoKategoria } from '@lemon/core-firebase/model/tuotenimikkeet'
import { ErrorResponse, KuukausiAikavali, LocalMonth, NumberMonth } from '@lemon/core/model/common'
import { AsiakkaanSopimuskaudenTila } from './asiakas'
import { KirjanpitajanTiimi, KirjanpitajanTiimikokonaisuus } from './kirjanpitaja'

export interface KuukaudenMuutosData {
  vuosikk: LocalMonth
  yhteensa: number
  plussaa: number
  miinusta: number
}

export interface AnnaLaskutusmuutosKuukausittainPyynto {
  aikavali: KuukausiAikavali
  rajoitettuKirjanpitajaanAvain: string
  rajoitettuTiimiinAvain: KirjanpitajanTiimi['avain']
  rajoitettuTiimiKokonaisuuteenAvain: KirjanpitajanTiimikokonaisuus['avain']
  rajoitettuTagiinAvain?: string
}

export interface AnnaLaskutusmuutosKuukausittainVastaus {
  muutosdata: KuukaudenMuutosData[]
}

export interface JatkuvanLaskutuksenKehitysVastaus {
  muutosdata: JatkuvanLaskutuksenKehitysTiedot[]
  lahteet?: JatkuvanLaskutuksenLahdeTieto[]
}

export interface LaskutuksenMuutos {
  summa: number
  kpl: number
}

export interface JatkuvanLaskutuksenKehitysTiedot {
  vuosi: number
  kuukausi: number
  edellinenKkLoppu: LaskutuksenMuutos
  paattyneetSopimukset: LaskutuksenMuutos
  poytalaatikkoonMenneet: LaskutuksenMuutos
  poytalaatikostaTulleet: LaskutuksenMuutos
  tauolleMenneet: LaskutuksenMuutos
  tauoltaTulleet: LaskutuksenMuutos
  hinnanKorotukset: LaskutuksenMuutos
  hinnanAlennukset: LaskutuksenMuutos
  sisaisetSiirrotMiinus: LaskutuksenMuutos
  sisaisetSiirrotPlus: LaskutuksenMuutos
  kkAlussaVoimaantulleet: LaskutuksenMuutos
  alkaneetSopimukset: LaskutuksenMuutos
  jatkuvaLaskutusYhteensa: LaskutuksenMuutos
  poistumaYhteensa: LaskutuksenMuutos
  lisaysYhteensa: LaskutuksenMuutos
  muutosEdelliseenKk: LaskutuksenMuutos
  tagirajaukseenTulleet?: LaskutuksenMuutos
  tagirajauksestaPoistuneet?: LaskutuksenMuutos
  lahteet?: JatkuvanLaskutuksenLahdeTieto[]
  yhtiomuotojakauma?: { [yhtiomuoto: string]: number }
  tauollaOlevatAsiakkaat?: {
    tauolla: number
    poytalaatikossa: number
  }
}

export interface JatkuvanLaskutuksenLahdeTieto {
  asiakasAvain: string
  nimi: string
  summa: number
}

export enum JatkuvanLaskutuksenKehitysKentta {
  EDELLISEN_KK_LOPPUSALDO = 1,
  PAATTYNEET_SOPIMUKSET = 2,
  POYTALAATIKKOON_MENNEET = 3,
  POYTALAATIKOSTA_TULLEET = 4,
  TAUOLLE_MENNEET = 5,
  TAUOLTA_TULLEET = 6,
  HINNAN_KOROTUKSET = 7,
  HINNAN_ALENNUKSET = 8,
  KK_ALUSSA = 9,
  ALKANEET_SOPIMUKSET = 10,
  JATKUVA_LASKUTUS_YHTEENSA = 11,
  MUUTOS_EDELLISEEN_KK = 12,
  SISAISET_SIIRROT_PLUS = 13,
  SISAISET_SIIRROT_MIINUS = 14,
  POISTUMA_YHTEENSA = 15,
  LISAYS_YHTEENSA = 16,
  TAGIRAJAUKSEEN_TULLEET = 17,
  TAGIRAJAUKSESTA_POISTUNEET = 18,
}

export interface MyyntiyhteenvetoJakaumallaPyynto extends AnnaLaskutusmuutosKuukausittainPyynto {
  tuote?: string
  onHyvitys?: boolean
  excludeTilastoKategoria?: TilastoKategoria
}

export interface MyyntiYhteenvetoVastaus {
  kuukausidata: MyyntiYhteenvetoTiedotProjektit[]
}

export interface YhteenvetoRivi {
  myynti: number
  hyvitykset: number
  yhteensa: number
}

export interface MyyntiYhteenvetoTiedotProjektit {
  vuosi: number
  kuukausi: number
  tuotteet: TilastotTuote[]
  lahteet?: MyyntiYhteenvetoLahdeTieto[]
  myyntiYhteensa?: YhteenvetoRivi
  palvelumyyntiYhteensa?: YhteenvetoRivi
}

export interface TilastotTuote {
  tuote: string
  nimi: string
  summa: number
  onHyvitys?: boolean
}

export interface MyyntiYhteenvetoLahdeTieto {
  /** Laskun avain */
  a: string
  /** Juurilaskun avain */
  j?: string | null
  /** Laskunumero */
  n: string
  /** Asiakkaan avain */
  av: string
  /** Summa (ALV 0%) */
  s: number
  /** Tuote */
  t?: string | null
}

export interface MyyntiYhteenvetoLahdeTietoNimitiedoilla extends MyyntiYhteenvetoLahdeTieto {
  asiakasNimi: string
}

export interface TilastotLahdeTieto {
  /** Asiakkaan avain */
  av: string
  /** Asiakkaiden lukumäärä */
  lkm: number
  /** Kuukausihinta hinnastosta */
  summa: number
}

export interface TilastotLahdeTietoNimitiedoilla extends TilastotLahdeTieto {
  asiakasNimi: string
}

export interface AnnaKuukaudenLaskutusJakaumallaPyynto {
  aikavali: KuukausiAikavali
  rajoitettuKirjanpitajaanAvain: string
  rajoitettuTiimiinAvain: KirjanpitajanTiimi['avain']
  palautaLahteet: boolean
}

export interface JatkuvaKuukaudenLaskutusJakaumallaPyynto extends AnnaLaskutusmuutosKuukausittainPyynto {
  palautaSolu?: JatkuvanLaskutuksenKehitysKentta[]
}

export interface AnnaKuukaudenLaskutusJakaumallaVastaus {
  jakaumadata: KuukaudenLaskutusJakaumallaTiedot[]
}

export interface KuukaudenLaskutusJakaumallaTiedot {
  vuosi: number
  kuukausi: number
  kertalaskutus: number
  jatkuvalaskutus: number
  kertahyvitykset: number
  jatkuvahyvitykset: number
  tuntematon: number
  yhteensa: number
  lahteet?: {
    laskunro: string
    summa: number
    lahetystyyppi: LaskunLahetystyyppi
    juurilaskuAvain: string
  }[]
}

export interface AnnaKuukaudenLaskutusKirjanpitajittainJakaumallaPyynto {
  aikavali: KuukausiAikavali
  rajoitettuKirjanpitajaanAvain: string
  rajoitettuTiimiinAvain: KirjanpitajanTiimi['avain']
  palautaLahteet: boolean
}

export interface AnnaKuukaudenLaskutusKirjanpitajittainJakaumallaVastaus {
  jakaumadata: KuukaudenLaskutusKirjanpitajittainJakaumallaTiedot[]
}

export interface KuukaudenLaskutusKirjanpitajittainJakaumallaTiedot extends KuukaudenLaskutusJakaumallaTiedot {
  kirjanpitajanAvain: string
  kirjanpitaja: string
}

export interface GetSalesReportDataRequest {
  month: NumberMonth
}
export interface GetSalesReportDataResponse extends ErrorResponse {
  data: SalesReportRow[]
}

export interface SalesReportRow {
  asiakasAvain: string
  asiakkaanNimi: string
  hinta: number
  salesPerson: string
}

export interface AnnaAsiakkaidenTilastotiedotPyynto {
  rajoitettuKirjanpitajaanAvain: string
  rajoitettuTiimiinAvain: KirjanpitajanTiimi['avain']
  /** jos annettu, palauttaa asiakkaiden hintojen muutokset annetulla aikavälillä */
  aikavali?: KuukausiAikavali
  rajoitettuTagiinAvain?: string | null
}
export interface AnnaAsiakkaidenTilastotiedotVastaus {
  tiedot: AsiakkaanTilastotiedot[]
}
export interface AsiakkaanTilastotiedot {
  /** Avain */
  a: string
  /** Id */
  i: number
  /** Nimi */
  n: string
  /** y-Tunnus */
  t: string
  /** Yritysmuoto */
  y: Yritysmuoto
  /** Nykyinen hinta */
  h1: number
  /** Tuleva hinta */
  h2: number
  /** Nykyinen kirjanpitaja */
  k: string
  hm: HinnanMuutos[]
  /** Sopimuksen tila */
  s: AsiakkaanSopimuskaudenTila
  /** Testiasiakas */
  q?: 1
}

export interface HinnanMuutos {
  localMonth: LocalMonth
  hinta: number
  muutos: number
  syy: string
}

export interface AnnaAsiakkaidenTilastotiedotKirjanpitajittainPyynto {
  rajoitettuKirjanpitajaanAvain: string
  rajoitettuTiimiinAvain: KirjanpitajanTiimi['avain']
  kuukausi: LocalMonth
  kuukausiAlku: LocalMonth
  kuukausiLoppu: LocalMonth
  rajoitettuTagiinAvain?: string | null
}
export interface AnnaAsiakkaidenTilastotiedotKirjanpitajittainVastaus {
  tiedot: AnnaAsiakkaidenTilastotiedotKirjanpitajittain[]
}
export interface AnnaAsiakkaidenTilastotiedotKirjanpitajittain {
  kirjanpitajaAvain: string
  jatkuvakk1: number
  lisaykset: number
  vahennykset: number
  yhtiomuodotTmi: number
  yhtiomuodotOy: number
  yhtiomuodotMuut: number
  kkJakauma: { [kk: string]: number }
  jatkuvakk2?: number // SET BY FRONT END ONLY
  keskimaarainenVeloitus?: number // SET BY FRONT END ONLY
  yhtiomuotoString?: string // SET BY FRONT END ONLY
  kirjanpitaja?: string // SET BY FRONT END ONLY
  asiakkaita?: number // SET BY FRONT END ONLY
}

// Jatkuva laskutus 1.1 Lisäykset Poistot Jatkuva laskutus 1.2 Asiakkaita 1.1 Keskimääräinen jatkuva laskutus 1.1 Yhtiömuodot 1.1

export enum TiimaritRaporttiTyyppi {
  JATKUVAN_LASKUTUKSEN_KEHITYS = 'jatkuvanlaskutuksenkehitys',
  MYYNTIYHTEENVETO = 'myyntiyhteenveto',
  TILASTOT = 'tilastot',
}

export interface TiimaritTilastotPyynto {
  tiimi: KirjanpitajanTiimi['avain']
  aikavali: KuukausiAikavali
  raportti?: TiimaritRaporttiTyyppi
  palautaSolu?: TiimaritJatkuvanLaskutuksenKehitysKentta[] | TiimaritMyyntiyhteenvetoKentta[] | TiimaritTilastotKentta[]
  kirjanpitaja?: string
  rajoitettuTagiinAvain?: string | null
}

export interface TiimaritTilastotVastaus {
  myyntiyhteenveto: KirjanpitajanMyyntiyhteenvetoTiedot[]
  myyntiyhteenvetoYhteensa?: KirjanpitajanMyyntiyhteenvetoYhteensa
  jatkuvanLaskutuksenKehitys: KirjanpitajanJatkuvanLaskutuksenKehitys[]
  jatkuvanLaskutuksenKehitysYhteensa?: KirjanpitajanJatkuvanLaskutuksenKehitysYhteensa
  tilastot: KirjanpitajanTilastotiedot[]
  tilastotYhteensa?: KirjanpitajanTilastotiedotYhteensa
  lahteet?: JatkuvanLaskutuksenLahdeTieto[] | MyyntiYhteenvetoLahdeTieto[] | TilastotLahdeTieto[]
}

export interface KirjanpitajanTiedot {
  avain: string
  nimi: string
}

export interface KirjanpitajanMyyntiyhteenvetoYhteensa {
  kuukausittainenPalvelumaksu: number
  jatkuvaluonteinenKertalaskutus: number
  kertalaskutus: number
  kertalaskutusYhteensa: number
  palvelulaskutusYhteensa: number
  palkkapalvelut: number
  ohjelmisto: number
  muuLaskutus: number
  yhteensa: number
  hyvitykset: number
}

export interface KirjanpitajanMyyntiyhteenvetoTiedot extends KirjanpitajanMyyntiyhteenvetoYhteensa {
  kirjanpitaja: string
}

export interface KirjanpitajanJatkuvanLaskutuksenKehitysYhteensa {
  edellinenKkLoppu: number
  paattyneetSopimukset: number
  muutoksetPoytalaatikoissa: number
  muutoksetTauoissa: number
  hinnanmuutokset: number
  sisaisetSiirrot: number
  muutoksetTagirajauksessa?: number
  alkaneetSopimukset: number
  jatkuvaLaskutusYhteensa: number
  muutosEdelliseenKuukauteen: number
  poistumaYhteensa: number
  lisaysYhteensa: number
}

export interface KirjanpitajanJatkuvanLaskutuksenKehitys extends KirjanpitajanJatkuvanLaskutuksenKehitysYhteensa {
  kirjanpitaja: string
}

export interface KirjanpitajanTilastotiedotYhteensa {
  tmiPalvelupaketti: PalvelupakettierittelyTiedot
  oyPalvelupaketti: PalvelupakettierittelyTiedot
  kasvuPalvelupaketti: number
  yhteensa: number
  laskutusYhteensa: number
  keskiveloitus: number
}

export interface KirjanpitajanTilastotiedot extends KirjanpitajanTilastotiedotYhteensa {
  kirjanpitaja: string
}

export interface PalvelupakettierittelyTiedot {
  yhteensa: number
  maksuperusteinen: number
  laskuperusteinen: number
}

export enum TiimaritMyyntiyhteenvetoKentta {
  KUUKAUSITTAINEN_PALVELUMAKSU = 1,
  JATKUVALUONTEINEN_KERTALASKUTUS = 2,
  KERTALASKUTUS = 3,
  KERTALASKUTUS_YHTEENSA = 4,
  PALVELULASKUTUS_YHTEENSA = 5,
  PALKKAPALVELUT = 6,
  OHJELMISTO = 7,
  MUU_LASKUTUS = 8,
  YHTEENSA = 9,
  HYVITYKSET = 10,
}

export enum TiimaritJatkuvanLaskutuksenKehitysKentta {
  EDELLINEN_KK_LOPPU = 1,
  PAATTYNEET_SOPIMUKSET = 2,
  MUUTOKSET_POYTALAATIKOISSA = 3,
  MUUTOKSET_TAUOISSA = 4,
  HINNANMUUTOKSET = 5,
  SISAISET_SIIRROT = 6,
  ALKANEET_SOPIMUKSET = 7,
  JATKUVA_LASKUTUS_YHTEENSA = 8,
  MUUTOS_EDELLISEEN_KUUKAUTEEN = 9,
  POISTUMA_YHTEENSA = 10,
  LISAYS_YHTEENSA = 11,
  MUUTOKSET_TAGIRAJAUKSESSA = 12,
}

export enum TiimaritTilastotKentta {
  TMI_PALVELUPAKETTI = 1,
  OY_PALVELUPAKETTI = 2,
  KASVU_PALVELUPAKETTI = 3,
  YHTEENSA = 4,
  LASKUTUS_YHTEENSA = 5,
  KERTAVELOITUS = 6,
}
